/**
 * /admin/audit — activity log (native oddits design).
 * @file src/web/features/admin/pages/audit.tsx
 *
 * Every tracked transaction — sign-ins/outs, entity + team changes, Tally
 * imports, profile edits, etc. — with who did it and when. Professional admins see
 * only their own professional's activity (scoped server-side). Row click opens a
 * detail drawer with the full record and a before/after diff.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import {
  Search, X, LogIn, LogOut, UserPlus, ShieldAlert, Building2, Pencil, Archive,
  ArchiveRestore, Trash2, KeyRound, UserCheck, UserX, Upload, Settings, Mail,
  Activity, Clock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { SettingsTabs } from '../components/SettingsTabs';
import { PageLoading } from '../components/PageLoading';
import { adminFetchJson } from '../lib/admin-api';

interface AuditRow {
  id: string;
  actorId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  actorType: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  oldValue: unknown;
  newValue: unknown;
  description: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

/* ── action → friendly label + icon + colour ───────────────────── */

const TONE = {
  ok: 'bg-ok-50 text-ok-600',
  danger: 'bg-danger-50 text-danger-600',
  warn: 'bg-warn-50 text-warn-600',
  brand: 'bg-highlighter-40 text-inkblack',
  info: 'bg-info-50 text-info-600',
  neutral: 'bg-ink-100 text-ink-600',
} as const;

const ACTIONS: Record<string, { label: string; icon: LucideIcon; tone: keyof typeof TONE }> = {
  'auth.login.success': { label: 'Signed in', icon: LogIn, tone: 'ok' },
  'auth.login.failure': { label: 'Failed sign-in', icon: ShieldAlert, tone: 'danger' },
  'auth.logout': { label: 'Signed out', icon: LogOut, tone: 'neutral' },
  'auth.register': { label: 'Registered', icon: UserPlus, tone: 'ok' },
  'auth.register.failure': { label: 'Failed registration', icon: ShieldAlert, tone: 'danger' },
  'entity.create': { label: 'Created entity', icon: Building2, tone: 'brand' },
  'entity.update': { label: 'Updated entity', icon: Pencil, tone: 'neutral' },
  'entity.archive': { label: 'Archived entity', icon: Archive, tone: 'warn' },
  'entity.unarchive': { label: 'Unarchived entity', icon: ArchiveRestore, tone: 'ok' },
  'entity.delete': { label: 'Deleted entity', icon: Trash2, tone: 'danger' },
  'entity.password.reset': { label: 'Reset entity password', icon: KeyRound, tone: 'warn' },
  'team.member.create': { label: 'Added team member', icon: UserPlus, tone: 'brand' },
  'team.member.update': { label: 'Updated team member', icon: Pencil, tone: 'neutral' },
  'team.member.activate': { label: 'Activated member', icon: UserCheck, tone: 'ok' },
  'team.member.deactivate': { label: 'Deactivated member', icon: UserX, tone: 'warn' },
  'team.member.password.reset': { label: 'Reset member password', icon: KeyRound, tone: 'warn' },
  'team.member.delete': { label: 'Removed team member', icon: Trash2, tone: 'danger' },
  'tally.import': { label: 'Imported Tally data', icon: Upload, tone: 'info' },
  'professional.profile.update': { label: 'Updated professional profile', icon: Pencil, tone: 'neutral' },
  'user.create': { label: 'Created user', icon: UserPlus, tone: 'brand' },
  'user.update': { label: 'Updated user', icon: Pencil, tone: 'neutral' },
  'user.delete': { label: 'Deleted user', icon: Trash2, tone: 'danger' },
  'user.password.reset': { label: 'Reset user password', icon: KeyRound, tone: 'warn' },
  'settings.update': { label: 'Updated settings', icon: Settings, tone: 'neutral' },
  'email.config.update': { label: 'Updated email config', icon: Settings, tone: 'neutral' },
  'email.test.send': { label: 'Sent test email', icon: Mail, tone: 'info' },
  'contact.message.sent': { label: 'Contact message', icon: Mail, tone: 'info' },
};

function describe(action: string) {
  return ACTIONS[action] ?? { label: action.replace(/\./g, ' '), icon: Activity, tone: 'neutral' as const };
}

function displayActor(row: AuditRow): string {
  if (row.actorName) return row.actorName;
  if (row.actorEmail) return row.actorEmail;
  if (row.actorType === 'system') return 'System';
  return 'Unknown';
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminAudit() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const load = useCallback(async (action: string) => {
    setError(null);
    try {
      const data = await adminFetchJson<{ items: AuditRow[] }>('/api/audit/list', { query: { action, limit: 200 } });
      setRows(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setRows([]);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => load(q), 300);
    return () => clearTimeout(id);
  }, [q, load]);

  return (
    <>
      <SEO title="Activity" />
      <AdminPageHeader title="Activity" breadcrumbs={[{ label: 'oddits' }, { label: 'Settings', href: '/admin/settings' }, { label: 'Activity' }]} />
      <SettingsTabs />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action (e.g. entity, login)…"
            className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-ink-300 focus-visible:outline-none" style={{ outline: 'none', boxShadow: 'none' }} />
        </div>
        {q && (
          <button onClick={() => setQ('')} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">
            <X className="h-4 w-4" /> Clear
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}

      {!rows ? (
        <PageLoading label="Loading activity" />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><Activity className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">No activity yet</h3>
          <p className="mt-1.5 text-sm text-ink-600">Actions like sign-ins, entity changes and imports will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Event</th>
                <th className="px-5 py-3 font-medium">Who</th>
                <th className="px-5 py-3 font-medium">Target</th>
                <th className="px-5 py-3 text-right font-medium">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {rows.map((r) => {
                const a = describe(r.action);
                return (
                  <tr key={r.id} onClick={() => setSelected(r)} className="cursor-pointer hover:bg-ink-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className={`grid size-8 shrink-0 place-items-center rounded-lg ${TONE[a.tone]}`}><a.icon className="h-4 w-4" /></span>
                        <div className="min-w-0">
                          <div className="font-medium text-ink-900">{a.label}</div>
                          {r.description && <div className="truncate text-xs text-ink-400">{r.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-600">{displayActor(r)}</td>
                    <td className="px-5 py-3 text-ink-500">{r.entityType ? r.entityType.replace(/_/g, ' ') : '—'}</td>
                    <td className="px-5 py-3 text-right text-ink-500" title={new Date(r.createdAt).toLocaleString()}>{relTime(r.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <AuditDrawer row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

const AuditDrawer = memo(function AuditDrawer({ row, onClose }: { row: AuditRow; onClose: () => void }) {
  const a = describe(row.action);
  const hasDiff = row.oldValue != null || row.newValue != null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-ink-200 bg-paper shadow-xl">
        <div className="flex items-start justify-between border-b border-ink-100 px-6 py-5">
          <div className="flex items-center gap-3">
            <span className={`grid size-11 place-items-center rounded-xl ${TONE[a.tone]}`}><a.icon className="h-5 w-5" /></span>
            <div>
              <h3 className="text-base font-semibold text-ink-900">{a.label}</h3>
              <p className="text-xs text-ink-400">{new Date(row.createdAt).toLocaleString()}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <DField label="Who">
            <div className="font-medium text-ink-800">{displayActor(row)}</div>
            {row.actorEmail && row.actorEmail !== displayActor(row) && <div className="text-xs text-ink-500">{row.actorEmail}</div>}
            <span className="mt-1 inline-block rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium capitalize text-ink-500">{row.actorType}</span>
          </DField>
          <DField label="Action"><span className="font-mono text-xs text-ink-600">{row.action}</span></DField>
          {row.entityType && <DField label="Target"><span className="font-mono text-xs text-ink-600">{row.entityType}{row.entityId ? `:${row.entityId}` : ''}</span></DField>}
          {row.description && <DField label="Details">{row.description}</DField>}
          <DField label="When"><Clock className="mr-1 inline h-3.5 w-3.5 text-ink-400" />{relTime(row.createdAt)}</DField>
          {row.ipAddress && <DField label="IP"><span className="font-mono text-xs text-ink-600">{row.ipAddress}</span></DField>}
          {row.userAgent && <DField label="Device"><span className="break-all text-xs text-ink-500">{row.userAgent}</span></DField>}

          {hasDiff && (
            <div className="space-y-2 border-t border-ink-100 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Changes</p>
              <JsonBlock label="Before" value={row.oldValue} />
              <JsonBlock label="After" value={row.newValue} />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
});

function DField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <div className="mt-1 text-sm text-ink-800">{children}</div>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-ink-50 p-2.5 text-xs text-ink-700">
        {value == null ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
